'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslations';
import { EXTERNAL_LINKS } from '@/constants/routes';
import './PricingSection.scss';

export default function PricingSection() {
  const { t } = useTranslation();

  return (
    <section
      id="pricing"
      className="pricing-section"
      aria-labelledby="pricing-section-title"
    >
      <div className="pricing-section__inner">
        <div className="pricing-section__header">
          <p className="pricing-section__label">{t('pricing.label')}</p>
          <h2 id="pricing-section-title" className="pricing-section__title">
            {t('pricing.title')}
          </h2>
        </div>

        <div className="pricing-section__cards">
          <article className="pricing-section__card">
            <div className="pricing-section__card-header">
              <div className="pricing-section__plan-icon">
                <Image
                  src="/starter-icon.svg"
                  alt={t('pricing.plans.starter.iconAlt')}
                  width={32}
                  height={32}
                />
              </div>

              <h3 className="pricing-section__plan-name">
                {t('pricing.plans.starter.name')}
              </h3>
              <p className="pricing-section__plan-description">
                {t('pricing.plans.starter.description')}
              </p>

              <p className="pricing-section__price">
                <span className="pricing-section__price-value">
                  {t('pricing.plans.starter.price')}
                </span>
                <span className="pricing-section__price-period">
                  {t('pricing.plans.starter.period')}
                </span>
              </p>

              <div className="pricing-section__badge-slot">
                <div className="pricing-section__badge">
                  <p className="pricing-section__badge-title">
                    {t('pricing.plans.starter.badgeLine1')}
                  </p>
                  <p className="pricing-section__badge-text">
                    {t('pricing.plans.starter.badgeLine2')}
                  </p>
                </div>
              </div>
            </div>

            <ul className="pricing-section__features">
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.starter.features.platform')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.starter.features.profile')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.starter.features.credit')}</span>
              </li>
            </ul>

            <Link
              href={EXTERNAL_LINKS.START_HIRING}
              className="pricing-section__cta pricing-section__cta--dark"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('pricing.plans.starter.cta')}
            </Link>
          </article>

          <article className="pricing-section__card pricing-section__card--featured">
            <div className="pricing-section__card-header">
              <div className="pricing-section__plan-icon">
                <Image
                  src="/growth-icon.svg"
                  alt={t('pricing.plans.growth.iconAlt')}
                  width={32}
                  height={32}
                />
              </div>

              <h3 className="pricing-section__plan-name">
                {t('pricing.plans.growth.name')}
              </h3>
              <p className="pricing-section__plan-description">
                {t('pricing.plans.growth.description')}
              </p>

              <p className="pricing-section__price">
                <span className="pricing-section__price-value">
                  {t('pricing.plans.growth.price')}
                </span>
              </p>
            </div>

            <ul className="pricing-section__features">
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.growth.features.starter')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.growth.features.credits')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.growth.features.placement')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.growth.features.matching')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.growth.features.chat')}</span>
              </li>
            </ul>

            <Link
              href={EXTERNAL_LINKS.START_HIRING}
              className="pricing-section__cta pricing-section__cta--pink"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('pricing.plans.growth.cta')}
            </Link>
          </article>

          <article className="pricing-section__card">
            <div className="pricing-section__card-header">
              <div className="pricing-section__plan-icon">
                <Image
                  src="/pro-icon.svg"
                  alt={t('pricing.plans.pro.iconAlt')}
                  width={32}
                  height={32}
                />
              </div>

              <h3 className="pricing-section__plan-name">
                {t('pricing.plans.pro.name')}
              </h3>
              <p className="pricing-section__plan-description">
                {t('pricing.plans.pro.description')}
              </p>

              <p className="pricing-section__price">
                <span className="pricing-section__price-value">
                  {t('pricing.plans.pro.price')}
                </span>
              </p>
            </div>

            <ul className="pricing-section__features">
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.pro.features.interviews')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.pro.features.matching')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.pro.features.analytics')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.pro.features.roles')}</span>
              </li>
              <li>
                <Image
                  src="/check-mark.svg"
                  alt=""
                  width={20}
                  height={20}
                  className="pricing-section__feature-icon"
                  aria-hidden
                />
                <span>{t('pricing.plans.pro.features.support')}</span>
              </li>
            </ul>

            <Link
              href={EXTERNAL_LINKS.CONTACT_EMAIL}
              className="pricing-section__cta pricing-section__cta--dark"
            >
              {t('pricing.plans.pro.cta')}
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
}
